import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  UserPlus, Users, Coins, Globe2, Sparkles, ArrowRight,
  ShieldCheck, FileText, Clock, Calendar, CheckCircle2,
  AlertTriangle, Trash2, KeyRound, ClipboardList, UserCheck,
} from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import { useMyCompany } from '@/lib/companies';
import { useEmployeeSummary } from '@/lib/employees';
import { countryFlag } from '@/lib/countries';
import { fadeInUp, sectionViewport, staggerContainer } from '@/components/landing/animations';
import { useNextActions, type NextAction, type Severity } from '@/lib/dashboard';
import { useCurrentUser } from '@/lib/auth';
import { useDemoStatus, useSeedDemo, useClearDemo } from '@/lib/demo';

export function AppHome() {
  const { t } = useTranslation();
  const company = useMyCompany();
  const summary = useEmployeeSummary();
  const total = summary.data?.headcount ?? 0;
  const me = useCurrentUser();
  const isAdmin = ['owner', 'admin'].includes(me.data?.role ?? '');
  const demo = useDemoStatus(isAdmin);
  const seedDemo = useSeedDemo();
  const clearDemo = useClearDemo();
  const fy = company.data
    ? (() => {
        const d = new Date();
        const f = company.data!.fiscalYearStartMonth ?? 1;
        return d.getMonth() + 1 >= f ? d.getFullYear() : d.getFullYear() - 1;
      })()
    : new Date().getFullYear();

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 max-w-7xl mx-auto">
        {/* Hero header card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-3xl glass-strong shadow-card-lift p-6 md:p-8"
        >
          <div aria-hidden className="pointer-events-none absolute -top-24 -end-24 h-72 w-72 rounded-full bg-gradient-to-br from-indigo-400/40 to-fuchsia-400/30 blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute -bottom-24 -start-24 h-72 w-72 rounded-full bg-gradient-to-br from-sky-300/30 to-emerald-300/20 blur-3xl" />

          <div className="relative flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t('appHome.eyebrow', { defaultValue: 'Workspace overview' })}</p>
              <h1 className="display serif-jp mt-1 text-3xl md:text-4xl font-semibold tracking-tight">
                {company.data?.name}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1">
                <span>FY{fy}</span>
                <span aria-hidden>·</span>
                <span className="num">{company.data?.reportingCurrency}</span>
                <span aria-hidden>·</span>
                <span>{company.data?.countries.length} {(company.data?.countries.length ?? 0) === 1 ? t('appHome.country', { defaultValue: 'country' }) : t('appHome.countries', { defaultValue: 'countries' })}</span>
                <span aria-hidden>·</span>
                <VerificationBadge status={company.data?.verificationStatus ?? 'pending'} />
              </p>
            </div>
            <Link
              to="/app/people"
              className="gradient-primary inline-flex h-11 items-center gap-2 px-5 rounded-xl text-white text-sm font-semibold shadow-glow hover:shadow-glow-pink hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
            >
              <UserPlus className="h-4 w-4" />
              {t('appHome.addEmployee', { defaultValue: 'Add employee' })}
            </Link>
          </div>
        </motion.div>

        {/* Stat tiles */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          <StatTile icon={Users}   accent="from-indigo-500 to-fuchsia-500" label={t('appHome.activeEmployees', { defaultValue: 'Active employees' })} value={String(total)} />
          <StatTile icon={Globe2}  accent="from-sky-500 to-indigo-500"    label={t('appHome.countries', { defaultValue: 'Countries' })} value={String(company.data?.countries.length ?? 0)} />
          <StatTile icon={Coins}   accent="from-emerald-500 to-sky-500"   label={t('appHome.plan', { defaultValue: 'Plan' })} value={(company.data?.plan ?? 'free').toUpperCase()} hint={company.data?.plan === 'free' ? t('appHome.freeSeats', { defaultValue: '10 free seats' }) : undefined} />
          <StatTile icon={Sparkles} accent="from-fuchsia-500 to-amber-500" label={t('appHome.aiModel', { defaultValue: 'AI model' })} value={(company.data?.aiModelDefault ?? '—').split('/')[1] || '—'} hint={t('appHome.workspaceDefault', { defaultValue: 'Workspace default' })} />
        </motion.div>

        {/* Sample-data banner (admin) — remove once real data is in */}
        {isAdmin && demo.data?.loaded && (
          <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-300/50 bg-amber-50/60 dark:bg-amber-500/10 px-4 py-2.5 text-xs">
            <Sparkles className="h-3.5 w-3.5 text-amber-600 shrink-0" aria-hidden />
            <span className="text-amber-800 dark:text-amber-300 font-medium flex-1 min-w-[180px]">
              {t('appHome.sampleLoaded', { defaultValue: 'Sample data is loaded so you can see how everything connects. Remove it when you add real employees.' })}
            </span>
            <button onClick={() => clearDemo.mutate()} disabled={clearDemo.isPending} className="font-semibold text-amber-700 dark:text-amber-300 hover:underline disabled:opacity-50">
              {clearDemo.isPending ? t('common.removing', { defaultValue: 'Removing…' }) : t('appHome.removeSample', { defaultValue: 'Remove sample data' })}
            </button>
          </div>
        )}

        {/* Next best action — the cohesion spine */}
        <NextBestAction />

        {/* Body */}
        {total === 0 ? (
          <motion.section
            initial="hidden"
            whileInView="visible"
            viewport={sectionViewport}
            variants={staggerContainer}
            className="mt-8 rounded-3xl glass-strong shadow-soft p-8 md:p-12 text-center"
          >
            <motion.div variants={fadeInUp} className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 grid place-items-center shadow-glow">
              <UserPlus className="h-6 w-6 text-white" />
            </motion.div>
            <motion.h2 variants={fadeInUp} className="mt-4 display serif-jp text-2xl font-semibold tracking-tight">{t('appHome.firstEmployeeTitle', { defaultValue: 'Add your first employee' })}</motion.h2>
            <motion.p variants={fadeInUp} className="mt-2 max-w-xl mx-auto text-sm text-muted-foreground text-pretty">
              {t('appHome.firstEmployeeBody', { defaultValue: "We'll guide them through the country-appropriate onboarding documents — JP 雇用契約書, US offer letter, UK PAYE, DE Lohnsteuer." })}
            </motion.p>
            <motion.div variants={fadeInUp} className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link to="/app/people" className="gradient-primary inline-flex h-11 items-center gap-2 px-5 rounded-xl text-white text-sm font-semibold shadow-glow hover:shadow-glow-pink transition">
                {t('appHome.addOne', { defaultValue: 'Add one employee' })}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/app/people" className="inline-flex h-11 items-center gap-2 px-5 rounded-xl border border-border text-foreground text-sm font-semibold hover:border-primary/40 hover:bg-primary/5 transition">
                <FileText className="h-4 w-4" />
                {t('appHome.bulkImport', { defaultValue: 'Bulk import' })}
              </Link>
              {isAdmin && (
                <button
                  onClick={() => seedDemo.mutate(undefined)}
                  disabled={seedDemo.isPending}
                  className="inline-flex h-11 items-center gap-2 px-5 rounded-xl border border-border text-foreground text-sm font-semibold hover:border-primary/40 hover:bg-primary/5 transition disabled:opacity-50"
                >
                  <Sparkles className="h-4 w-4" />
                  {seedDemo.isPending ? t('appHome.loadingSample', { defaultValue: 'Loading sample…' }) : t('appHome.loadSample', { defaultValue: 'Load sample data' })}
                </button>
              )}
            </motion.div>
          </motion.section>
        ) : (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="lg:col-span-2 rounded-3xl border border-border bg-card p-6 shadow-soft"
            >
              <div className="flex items-baseline justify-between">
                <h2 className="text-lg font-semibold tracking-tight">{t('appHome.headcountByCountry')}</h2>
                <span className="num text-xs text-muted-foreground">{total} {t('appHome.totalPeople', { defaultValue: 'people' })}</span>
              </div>
              <div className="mt-5 space-y-3">
                {(summary.data?.byCountry ?? []).map((c, i) => {
                  const pct = total > 0 ? Math.round((c.count / total) * 100) : 0;
                  return (
                    <motion.div
                      key={c.country_code}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + i * 0.05 }}
                      className="flex items-center gap-3"
                    >
                      <Link
                        to="/app/people"
                        className="flex items-center gap-3 flex-1 min-w-0 group hover:opacity-80 transition"
                      >
                        <span className="text-base w-6">{countryFlag(c.country_code)}</span>
                        <span className="text-sm text-foreground/80 flex-1 truncate group-hover:text-primary transition">{c.country_code}</span>
                      </Link>
                      <div className="w-40 h-1.5 bg-muted rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500"
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.8, delay: 0.2 + i * 0.05, ease: [0.16, 0.84, 0.44, 1] as const }}
                        />
                      </div>
                      <span className="num text-xs font-semibold w-6 text-end">{c.count}</span>
                    </motion.div>
                  );
                })}
              </div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 }}
              className="rounded-3xl border border-border bg-card p-6 shadow-soft"
            >
              <h2 className="text-lg font-semibold tracking-tight">{t('appHome.quickActions', { defaultValue: 'Quick actions' })}</h2>
              <div className="mt-4 space-y-2">
                {[
                  { to: '/app/payroll',      Icon: Coins,         label: t('appHome.qaRunPayroll',   { defaultValue: 'Run payroll' }) },
                  { to: '/app/compliance',   Icon: ShieldCheck,   label: t('appHome.qaCompliance',   { defaultValue: 'Review compliance' }) },
                  { to: '/app/documents',    Icon: FileText,      label: t('appHome.qaDocuments',    { defaultValue: 'Open documents' }) },
                  { to: '/app/hiring',       Icon: UserPlus,      label: t('appHome.qaHiring',       { defaultValue: 'Hiring' }) },
                  { to: '/app/time-off',     Icon: Clock,         label: t('appHome.qaTimeOff',      { defaultValue: 'Time off' }) },
                  { to: '/app/approvals',    Icon: CheckCircle2,  label: t('appHome.qaApprovals',    { defaultValue: 'Approvals' }) },
                  { to: '/app/year-end',     Icon: Calendar,      label: t('appHome.qaYearEnd',      { defaultValue: 'Year-end' }) },
                  { to: '/app/border-buddy', Icon: Sparkles,      label: t('appHome.qaBorderBuddy',  { defaultValue: 'Border Buddy' }) },
                ].map((a) => (
                  <Link
                    key={a.to}
                    to={a.to}
                    className="group flex items-center gap-3 rounded-xl border border-border px-3 py-2.5 hover:border-primary/40 hover:bg-primary/5 transition"
                  >
                    <span className="grid h-8 w-8 place-items-center rounded-lg bg-muted text-foreground/70 group-hover:bg-primary/15 group-hover:text-primary transition">
                      <a.Icon className="h-4 w-4" />
                    </span>
                    <span className="text-sm font-medium flex-1">{a.label}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition" />
                  </Link>
                ))}
              </div>
            </motion.section>
          </div>
        )}
      </div>
    </AppShell>
  );
}

const ACTION_META: Record<string, { icon: React.ComponentType<{ className?: string }>; title: string; cta: string }> = {
  approvals:         { icon: CheckCircle2,  title: '{{count}} approval awaiting your sign-off',            cta: 'Review' },
  complianceOverdue: { icon: AlertTriangle, title: '{{count}} overdue compliance item — act now',         cta: 'Resolve' },
  myNumberDue:       { icon: Trash2,        title: '{{count}} My Number past its retention window',        cta: 'Dispose' },
  residencyExceeded: { icon: Globe2,        title: '{{count}} employee over the 183-day residency threshold', cta: 'Review' },
  payrollAwaiting:   { icon: Coins,         title: '{{count}} payroll run ready to approve',               cta: 'Review payroll' },
  jpMissingMyNumber: { icon: KeyRound,      title: '{{count}} JP employee missing マイナンバー',           cta: 'Collect' },
  complianceOpen:    { icon: ShieldCheck,   title: '{{count}} open compliance item',                       cta: 'Review' },
  unlinkedEmployees: { icon: UserCheck,     title: '{{count}} employee without a login account',           cta: 'Invite' },
  openSurveys:       { icon: ClipboardList, title: '{{count}} open survey',                                cta: 'View' },
};

const SEV_STYLE: Record<Severity, { chip: string; icon: string }> = {
  high:   { chip: 'bg-rose-500/10 text-rose-700 dark:text-rose-400',     icon: 'text-rose-500' },
  medium: { chip: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',  icon: 'text-amber-500' },
  low:    { chip: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-400', icon: 'text-indigo-500' },
};

function NextBestAction() {
  const { t } = useTranslation();
  const q = useNextActions();
  const actions = q.data?.actions ?? [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="mt-4 rounded-2xl border border-border bg-card px-5 py-4 shadow-soft"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-semibold text-foreground">{t('appHome.nextBestAction', { defaultValue: 'Next best action' })}</h2>
      </div>

      {q.isLoading && <p className="mt-3 text-sm text-muted-foreground">{t('common.loading', { defaultValue: 'Loading…' })}</p>}

      {!q.isLoading && actions.length === 0 && (
        <p className="mt-3 text-sm text-muted-foreground flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
          {t('appHome.allCaughtUp', { defaultValue: "You're all caught up — nothing needs your attention right now." })}
        </p>
      )}

      {actions.length > 0 && (
        <ul className="mt-3 space-y-2">
          {actions.map((a: NextAction, i: number) => {
            const meta = ACTION_META[a.key] ?? { icon: Sparkles, title: a.key, cta: 'Open' };
            const sev = SEV_STYLE[a.severity];
            const Icon = meta.icon;
            return (
              <li key={a.key}>
                <Link
                  to={a.linkPath}
                  className={`group flex items-center gap-3 rounded-xl border px-3 py-2.5 transition hover:border-primary/40 hover:bg-primary/5 ${i === 0 ? 'border-primary/30 bg-primary/5' : 'border-border'}`}
                >
                  <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${sev.chip}`}>
                    <Icon className={`h-4 w-4 ${sev.icon}`} />
                  </span>
                  <span className="flex-1 min-w-0 text-sm font-medium text-foreground">
                    {t(`appHome.nextAction.${a.key}`, { count: a.count, defaultValue: meta.title })}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary shrink-0">
                    {t(`appHome.nextAction.${a.key}_cta`, { defaultValue: meta.cta })}
                    <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition rtl:rotate-180" />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </motion.div>
  );
}

function StatTile({
  icon: Icon, accent, label, value, hint,
}: {
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <motion.div
      variants={fadeInUp}
      whileHover={{ y: -3 }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-soft hover:shadow-card-lift transition"
    >
      <div className={`pointer-events-none absolute -top-12 -end-12 h-32 w-32 rounded-full bg-gradient-to-br ${accent} opacity-10 blur-2xl group-hover:opacity-25 transition-opacity duration-500`} />
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
          <p className="num mt-2 text-3xl font-bold tracking-tight">{value}</p>
          {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
        </div>
        <div className={`inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-glow`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </motion.div>
  );
}

function VerificationBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  if (status === 'verified')        return <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">● {t('verification.verified', { defaultValue: 'Verified' })}</span>;
  if (status === 'pending_review')  return <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">● {t('verification.inReview', { defaultValue: 'In review' })}</span>;
  if (status === 'rejected')        return <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400">● {t('verification.rejected', { defaultValue: 'Rejected' })}</span>;
  return <span className="inline-flex items-center gap-1 text-muted-foreground">● {t('verification.pending', { defaultValue: 'Pending' })}</span>;
}
